'use client';

import React, {
  forwardRef,
  ReactNode,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import {
  Item,
  Content,
  Header,
  Trigger,
  Root,
  type AccordionContentProps as AccordionContentInnerProps,
  type AccordionItemProps as AccordionItemInnerProps,
} from '@radix-ui/react-accordion';
import { motion, useInView } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { cn } from '@/utils/cn';
import { FeatureDataProps } from '../data/features';
import defaultFeatures from '../data/features';

type AccordionItemProps = {
  children: React.ReactNode;
  className?: string;
} & AccordionItemInnerProps;

const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Item
      className={cn(
        'mt-px overflow-hidden focus-within:relative focus-within:z-10',
        className
      )}
      {...props}
      ref={forwardedRef}
    >
      {children}
    </Item>
  )
);
AccordionItem.displayName = 'AccordionItem';

type AccordionTriggerProps = {
  children: React.ReactNode;
  className?: string;
};

const AccordionTrigger = forwardRef<HTMLButtonElement, AccordionTriggerProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Header className='flex'>
      <Trigger
        className={cn(
          'group flex min-h-[60px] flex-1 cursor-pointer items-start justify-between px-5 py-3 pb-1 text-[15px] leading-normal outline-none',
          className
        )}
        {...props}
        ref={forwardedRef}
      >
        {children}
      </Trigger>
    </Header>
  )
);
AccordionTrigger.displayName = 'AccordionTrigger';

type AccordionContentProps = {
  children: ReactNode;
  className?: string;
} & AccordionContentInnerProps;

const AccordionContent = forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Content
      className={cn(
        'overflow-hidden text-[15px] font-medium data-[state=closed]:animate-slide-up data-[state=open]:animate-slide-down',
        className
      )}
      {...props}
      ref={forwardedRef}
    >
      <div className='px-5 pb-3 pt-1 leading-relaxed'>{children}</div>
    </Content>
  )
);
AccordionContent.displayName = 'AccordionContent';

type FeatureProps = {
  collapseDelay?: number;
  ltr?: boolean;
  linePosition?: 'left' | 'right';
  translations?: {
    title: string;
    features: {
      [key: string]: {
        title: string;
        content: string;
      };
    };
  };
};

const Feature = ({
  collapseDelay = 5000,
  ltr = false,
  linePosition = 'left',
  translations,
}: FeatureProps) => {
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const [features, setFeatures] = useState<FeatureDataProps[]>(defaultFeatures);
  const pathname = usePathname();

  const carouselRef = useRef<HTMLUListElement>(null);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    amount: 0.5,
  });

  // Extract current page type
  const getPageType = useCallback(() => {
    const path = pathname.split('/');
    // Find the last non-empty segment in the path
    for (let i = path.length - 1; i >= 0; i--) {
      if (path[i] && path[i] !== '(image-rmw)') {
        return path[i];
      }
    }
    return 'watermark-removal'; // Default value
  }, [pathname]);

  // Dynamically load feature data
  useEffect(() => {
    const loadFeatures = async () => {
      const pageType = getPageType();

      try {
        // Dynamically import feature data for the current page
        const featuresModule = await import(`../../${pageType}/data/features`);

        // If translations exist, override the titles and content
        let loadedFeatures: FeatureDataProps[] = featuresModule.default;

        if (translations?.features) {
          loadedFeatures = loadedFeatures.map((feature: FeatureDataProps) => ({
            ...feature,
            title:
              translations.features[`feature${feature.id}`]?.title ||
              feature.title,
            content:
              translations.features[`feature${feature.id}`]?.content ||
              feature.content,
          }));
        }

        setFeatures(loadedFeatures);
      } catch (error) {
        // If import fails, use default feature data with translations if available
        console.warn(
          `Failed to load features for ${pageType}, using default features instead.`
        );

        let fallbackFeatures: FeatureDataProps[] = defaultFeatures;

        if (translations?.features) {
          fallbackFeatures = fallbackFeatures.map(
            (feature: FeatureDataProps) => ({
              ...feature,
              title:
                translations.features[`feature${feature.id}`]?.title ||
                feature.title,
              content:
                translations.features[`feature${feature.id}`]?.content ||
                feature.content,
            })
          );
        }

        setFeatures(fallbackFeatures);
      }
    };

    loadFeatures();
  }, [getPageType, translations]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isInView) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(-1);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isInView]);

  const scrollToIndex = (index: number) => {
    if (carouselRef.current) {
      const card = carouselRef.current.querySelectorAll('.card')[index];
      if (card) {
        const cardRect = card.getBoundingClientRect();
        const carouselRect = carouselRef.current.getBoundingClientRect();
        const offset =
          cardRect.left -
          carouselRect.left -
          (carouselRect.width - cardRect.width) / 2;

        carouselRef.current.scrollTo({
          left: carouselRef.current.scrollLeft + offset,
          behavior: 'smooth',
        });
      }
    }
  };

  // interval for changing images
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex !== undefined ? (prevIndex + 1) % features.length : 0
      );
    }, collapseDelay);

    return () => clearInterval(timer);
  }, [collapseDelay, currentIndex, features.length]);

  useEffect(() => {
    const handleAutoScroll = () => {
      const nextIndex =
        (currentIndex !== undefined ? currentIndex + 1 : 0) % features.length;
      scrollToIndex(nextIndex);
    };

    const autoScrollTimer = setInterval(handleAutoScroll, collapseDelay);

    return () => clearInterval(autoScrollTimer);
  }, [collapseDelay, currentIndex, features.length]);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      const handleScroll = () => {
        const scrollLeft = carousel.scrollLeft;
        const cardWidth = carousel.querySelector('.card')?.clientWidth || 0;
        const newIndex = Math.min(
          Math.floor(scrollLeft / cardWidth),
          features.length - 1
        );
        setCurrentIndex(newIndex);
      };
      const controller = new AbortController();
      carousel.addEventListener('scroll', handleScroll, {
        signal: controller.signal,
      });
      return () => controller.abort();
    }
  }, [features.length]);

  const sectionTitle = translations?.title || 'We are the best AI tool for...';

  return (
    <section ref={ref} id='features' className='section-container lg:mt-20'>
      <div className='container flex w-full flex-col items-center justify-center'>
        <div className='max-w-8xl mx-auto grid h-full grid-cols-5 gap-x-20'>
          <div
            className={`col-span-5 md:col-span-3 ${
              ltr ? 'lg:order-2 lg:justify-end' : 'justify-start'
            }`}
          >
            <Root
              className='w-full max-w-full sm:max-w-[450px] lg:max-w-[600px]'
              type='single'
              defaultValue={`item-${currentIndex}`}
              value={`item-${currentIndex}`}
              onValueChange={(value) =>
                setCurrentIndex(Number(value.split('-')[1]))
              }
            >
              <h2 className='mb-6 ml-6 w-full text-left text-2xl font-semibold lg:ml-0 lg:text-4xl xl:text-[44px]'>
                {sectionTitle}
              </h2>

              <div className='h-[100px] lg:h-[420px]'>
                {features.map((item, index) => (
                  <div
                    key={item.id}
                    className={`w-full ${currentIndex !== index ? 'hidden lg:block' : ''}`}
                  >
                    <div className='flex w-full'>
                      <span className='h-[1px] w-1/12 lg:hidden' />
                      <AccordionItem
                        className='relative mb-4 mt-4 w-full first:mt-0 last:mb-0 lg:mb-6 lg:mt-6'
                        value={`item-${index}`}
                      >
                        <div
                          className={`absolute bottom-0 top-0 h-full w-2 overflow-hidden rounded-lg bg-neutral-300/50 dark:bg-neutral-300/30 ${
                            linePosition === 'right'
                              ? 'left-auto right-0'
                              : 'left-0 right-auto'
                          }`}
                        >
                          <div
                            className={`absolute left-0 top-0 w-full ${
                              currentIndex === index ? 'h-full' : 'h-0'
                            } origin-top bg-purple-600 transition-all ease-linear dark:bg-white`}
                            style={{
                              transitionDuration:
                                currentIndex === index
                                  ? `${collapseDelay}ms`
                                  : '0s',
                            }}
                          />
                        </div>
                        <AccordionTrigger
                          className={`text-left text-base font-semibold leading-tight md:text-xl lg:text-2xl ${
                            currentIndex === index ? 'text-purple-600' : ''
                          } py-2 lg:py-3`}
                        >
                          <span className='block w-full max-w-full hyphens-auto pr-6 text-lg md:text-2xl lg:text-2xl'>
                            {item.title}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className='hidden text-sm font-normal md:text-base lg:block lg:text-lg'>
                          <p className='leading-normal'>{item.content}</p>
                        </AccordionContent>
                      </AccordionItem>
                    </div>
                  </div>
                ))}
              </div>
            </Root>
          </div>

          <div
            className={`col-span-5 h-[400px] min-h-[200px] w-full lg:col-span-2 lg:h-[420px] lg:w-fit ${
              ltr && 'lg:order-1'
            }`}
          >
            {features[currentIndex]?.image ? (
              <div className='flex h-full w-full justify-center lg:justify-start'>
                <span className='h-[2px] w-0 lg:hidden' />
                <motion.img
                  key={currentIndex}
                  src={features[currentIndex].image}
                  alt='feature'
                  className='mt-2 h-[400px] w-full max-w-[90%] rounded-xl object-contain p-1 lg:mt-0 lg:h-[450px] lg:w-auto'
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                />
              </div>
            ) : (
              <div className='aspect-auto h-full w-full rounded-xl border border-neutral-300/50 bg-gray-200 p-1' />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

interface FeatureSectionProps {
  translations?: {
    title: string;
    features: {
      [key: string]: {
        title: string;
        content: string;
      };
    };
  };
}

export function FeatureSection({ translations }: FeatureSectionProps = {}) {
  return (
    <Feature
      collapseDelay={5000}
      linePosition='left'
      translations={translations}
    />
  );
}
